<script setup>
import { computed } from 'vue';
import { intlLocale, t } from '../i18n';
import { formatMoney, toNumber } from '../format';

// How much is left to deposit to IIS this year for the largest deduction (docs/iis/SPEC.md, decision 8).
// The limit is per person: the numbers cover all IIS accounts.
const props = defineProps({
  // iis from GET /portfolios: { year, limit, deposited, loaded, accounts: { id: sum } }
  iis: {
    type: Object,
    required: true,
  },
  // Desktop: full width of the card, smaller text.
  compact: {
    type: Boolean,
    default: false,
  },
});

const limit = computed(() => toNumber(props.iis.limit) ?? 0);
const deposited = computed(() => toNumber(props.iis.deposited) ?? 0);
const left = computed(() => Math.max(0, limit.value - deposited.value));
const done = computed(() => left.value === 0);
const filled = computed(() => (limit.value > 0 ? Math.min(100, (deposited.value / limit.value) * 100) : 0));

// The limit is a round sum: "400 000 ₽", without kopecks.
const limitText = computed(() => new Intl.NumberFormat(intlLocale(), {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
}).format(limit.value));
</script>

<template>
  <div :class="compact ? 'rounded-md bg-slate-800/60 px-4 py-3' : 'mt-4 rounded-2xl bg-slate-800 px-4 py-3'">
    <p :class="compact ? 'text-xs text-slate-400' : 'text-[0.8125rem] text-slate-400'">
      {{ t('iis.title', { year: iis.year }) }}
    </p>
    <p
      v-if="!iis.loaded"
      :class="compact ? 'mt-1 text-sm text-slate-400' : 'mt-1 text-[0.9375rem] text-slate-400'"
    >
      {{ t('iis.notLoaded') }}
    </p>
    <template v-else>
      <p
        class="mt-1 font-medium tabular-nums"
        :class="[compact ? 'text-sm' : 'text-[1.0625rem] leading-6', done ? 'text-emerald-400' : 'text-slate-100']"
      >
        {{ done ? t('iis.done', { year: iis.year }) : t('iis.left', { sum: formatMoney(left, 'RUB') }) }}
      </p>
      <p
        v-if="!done"
        class="text-slate-500"
        :class="compact ? 'text-xs' : 'text-[0.8125rem]'"
      >
        {{ t('iis.forDeduction') }}
      </p>
      <div
        class="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-700"
        role="progressbar"
        :aria-valuenow="Math.round(filled)"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="t('iis.title', { year: iis.year })"
      >
        <div
          class="h-full rounded-full"
          :class="done ? 'bg-emerald-500' : 'bg-slate-300'"
          :style="{ width: `${filled}%` }"
        />
      </div>
      <div class="mt-1 flex justify-between gap-3 text-[0.625rem] leading-3 tabular-nums text-slate-500">
        <span>{{ formatMoney(iis.deposited, 'RUB') }}</span>
        <span>{{ limitText }}</span>
      </div>
    </template>
  </div>
</template>
